import React, { useCallback } from 'react';

import { ColumnProps } from 'antd/es/table';
import { parseISO } from 'date-fns';

import useGetShipmentHistoryEvents from '../hooks/useGetShipmentHistoryEvents';
import StyledTable from '@/components/tables/StyledTable';
import dates from '@/helpers/dates';
import useAntdColumns from '@/hooks/useAntdColumns';

export interface IshipmentHistoryTableRow {
  eventName: string;
  boxEvent: string;
  boxDate: string;
  senderEvent: string;
  commandType: string;
  senderDate: string;
  key: string;
  rowFull: boolean;
}

const EventsTable = ({ id }) => {
  const historyEvents = useGetShipmentHistoryEvents(id);

  const formatTable = useCallback((events) => {
    // Group events by eventName
    const groupedEvents = events.reduce(
      (acc, event) => {
        const eventName = event.payload.To;

        if (!acc[eventName]) {
          acc[eventName] = [];
        }

        acc[eventName].push(event);

        return acc;
      },
      {} as Record<string, typeof events>
    );

    // Create one row per group
    return Object.entries(groupedEvents).map(([eventName, eventGroup]) => {
      const typedEventGroup = eventGroup as typeof events;

      // Find product/eventBus event
      const productEvent = typedEventGroup.find(
        (e) => e.changeType === 'product' || e.changeType === 'eventBus'
      );

      // Find system event (lpaccount, etc.)
      const systemEvent = typedEventGroup.find(
        (e) => e.changeType !== 'product' && e.changeType !== 'eventBus'
      );

      // Get CommandTypeName from the first event that has one
      const commandType =
        typedEventGroup.find((e) => e.payload.CommandTypeName)?.payload
          .CommandTypeName || 'N/A';

      return {
        eventName,
        boxEvent: productEvent
          ? `Product ID : ${productEvent.changedBy}`
          : 'N/A',
        boxDate: productEvent
          ? dates.formatDate(parseISO(productEvent.changedAt))
          : '',
        senderEvent: systemEvent
          ? `${systemEvent.changeType} - ${systemEvent.changedBy}`
          : 'N/A',
        senderDate: systemEvent
          ? dates.formatDate(parseISO(systemEvent.changedAt))
          : '',
        commandType,
        key: typedEventGroup[0].id,
        rowFull: !!productEvent && !!systemEvent,
      };
    });
  }, []);

  const dataSource = formatTable(historyEvents);

  const columnsKeys = [
    'eventName',
    'senderEvent',
    'commandType',
    'boxEvent',
    'boxDate',
  ];
  const columns: ColumnProps<any>[] = useAntdColumns({
    columnsKeys,
    addDefaultColumns: false,
    columnsSpecialProps: {
      eventName: {
        title: 'Event',
        dataIndex: 'eventName',
      },
      senderEvent: {
        title: 'System',
        dataIndex: 'senderEvent',
      },
      commandType: {
        title: 'Command type',
        dataIndex: 'commandType',
      },
      senderDate: {
        title: 'System date',
        dataIndex: 'senderDate',
      },
      boxEvent: {
        title: 'Product',
        dataIndex: 'boxEvent',
      },
      boxDate: {
        title: 'Product date',
        dataIndex: 'boxDate',
      },
    },
  });

  return (
    <StyledTable
      dataSource={dataSource}
      size="small"
      columns={columns}
      pagination={false}
    />
  );
};

export default EventsTable;
